import { ChangeDetectorRef, Component, inject, OnInit, ViewChild } from '@angular/core';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { DatatableColumn, DatatableRow } from '../../../shared/tables/datatable/datatable.models';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ButtonStyles } from '../../../button/button.component';
import { TranslateService } from '@ngx-translate/core';
import { DateAgoPipe } from '../../../shared/pipes/date-ago.pipe';
import { PstnCallingUsageReportResource } from './pstn-calling-usage-report.resource';
import {
  AccountType,
  CallRecordsDatabaseEntriesInfo,
  DowngradeOrUpgradeInsight,
  MicrosoftCallingPlanType,
  PstnCallingNumberType,
  PstnConnectivityType,
  SuggestedProduct,
  TeamsPstnCallingUsageDetail,
} from '../../../shared/models/generated/ziroModels';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { ToastService } from '../../../shared/services/toast.service';
import { DatatableFilterService } from '../../../shared/tables/datatable/datatable-filter.service';
import { DatePipe } from '@angular/common';
import { TelephoneNumberFilter } from '../../../shared/filters/telephone-number.filter';
import { numericizePhoneNumberNoCountry } from '../../../admin/order-numbers/phone-number-format.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { map } from 'rxjs/operators';
import { CallRecordsDatabaseEntriesResource } from '../shared/call-records-database-entries.resource';
import { combineLatest } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { FilterSelectOption } from '../../../shared/tables/datatable/filter-select/filter-select.component';
import moment from 'moment';
import { DatatableComponent } from '../../../shared/tables/datatable/datatable.component';

interface PstnCallingUsageRow extends DatatableRow {
  user: string;
  displayName: string;
  accountType: string;
  suggestedProduct: string;
  phoneNumber: string;
  callingPlan: string;
  pstnType: string;
  suggestedChanges: string;
  totalUtilizationIncomingCalls: number;
  totalUtilizationOutgoingCalls: number;
  totalUtilizationIncomingMinutes: number;
  totalUtilizationOutgoingMinutes: number;
  monthlyUtilizationIncomingCalls: number;
  monthlyUtilizationOutgoingCalls: number;
  monthlyUtilizationIncomingMinutes: number;
  monthlyUtilizationOutgoingMinutes: number;
}

@Component({
  selector: 'ziro-pstn-calling-usage-report',
  templateUrl: './pstn-calling-usage-report.component.html',
  styleUrls: ['../../reporting.scss'],
  providers: [PstnCallingUsageReportResource, DatePipe],
  standalone: false,
})
export class PstnCallingUsageReportComponent implements OnInit {
  @ViewChild('datatable') datatableComponent: DatatableComponent<PstnCallingUsageRow>;

  private _breadcrumbService = inject(BreadcrumbsService);
  private _bottomNavService = inject(BottomNavService);
  private _translateService = inject(TranslateService);
  private _dateAgoPipe = inject(DateAgoPipe);
  private _pstnCallingUsageReportResource = inject(PstnCallingUsageReportResource);
  private _callRecordsDatabaseEntriesResource = inject(CallRecordsDatabaseEntriesResource);
  private _toastService = inject(ToastService);
  private _datatableFilterService = inject(DatatableFilterService);
  private _datePipe = inject(DatePipe);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _route = inject(ActivatedRoute);
  private _changeDetectorRef = inject(ChangeDetectorRef);

  ziroIcons = ZiroIcons;
  buttonStyles = ButtonStyles;
  isLoading = true;
  tableDataAutomation = 'pstn-calling-usage-report-table';
  infoBoxParams: { numCallRecords: string; startDate: string; endDate: string; numDays: string };
  errorAlert: string;

  accountTypeFilterOptions: FilterSelectOption[] = [];
  suggestedCallingProductFilterOptions: FilterSelectOption[] = [];
  pstnTypeFilterOptions: FilterSelectOption[] = [];
  callingPlanFilterOptions: FilterSelectOption[] = [];
  suggestedChangesFilterOptions: FilterSelectOption[] = [];

  datatableRows = [] as PstnCallingUsageRow[];
  datatableCols: DatatableColumn<PstnCallingUsageRow>[] = [
    {
      name: 'user',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.user.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },
    {
      name: 'displayName',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.display_name.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },
    {
      name: 'phoneNumber',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.phone_number.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },
    {
      name: 'accountType',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },
    {
      name: 'pstnType',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },
    {
      name: 'callingPlan',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.label',
      subLabel: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
    },

    {
      name: 'suggestedProduct',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.ai_insights.label',
    },
    {
      name: 'suggestedChanges',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.ai_insights.label',
      columnCharacterWidth: 25,
    },
    {
      name: 'monthlyUtilizationIncomingCalls',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_calls.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
    },
    {
      name: 'monthlyUtilizationOutgoingCalls',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_calls.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
    },
    {
      name: 'monthlyUtilizationIncomingMinutes',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_minutes.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
    },
    {
      name: 'monthlyUtilizationOutgoingMinutes',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_minutes.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
    },
    {
      name: 'totalUtilizationIncomingCalls',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_calls.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
    },
    {
      name: 'totalUtilizationOutgoingCalls',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_calls.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
    },
    {
      name: 'totalUtilizationIncomingMinutes',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_minutes.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
    },
    {
      name: 'totalUtilizationOutgoingMinutes',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_minutes.label',
      subLabel: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
    },
  ];

  ACCOUNT_TYPE_NAME_MAP: Map<AccountType, string> = new Map([
    [AccountType.USER, 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.user'],
    [
      AccountType.RESOURCE_ACCOUNT,
      'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.resource_account',
    ],
    [AccountType.SFB_ON_PREM_USER, 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.on_prem'],
    [AccountType.UNKNOWN, 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.unknown'],
    [AccountType.GUEST, 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.guest'],
    [
      AccountType.UPN_NOT_IN_TEAMS,
      'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.upn_not_in_teams',
    ],
    [AccountType.INELIGIBLE_USER, 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type_filter.ineligible'],
  ]);
  SUGGESTED_CALLING_PRODUCT_MAP: Map<SuggestedProduct, string> = new Map([
    [
      SuggestedProduct.SHARED_CALLING,
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.shared_calling.label',
    ],
    [
      SuggestedProduct.PER_MINUTE,
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.per_minute_calling.label',
    ],
    [
      SuggestedProduct.BUNDLED_MINUTES,
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.bundled_minutes.label',
    ],
    [
      SuggestedProduct.NOT_APPLICABLE,
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.not_applicable.label',
    ],
  ]);
  PSTN_TYPE_NAME_MAP: Map<PstnConnectivityType, string> = new Map([
    [PstnConnectivityType.DIRECT_ROUTING, 'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.direct_routing'],
    [
      PstnConnectivityType.OPERATOR_CONNECT,
      'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.operator_connect',
    ],
    [
      PstnConnectivityType.MICROSOFT_CALLING_PLANS,
      'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.microsoft_calling_plan',
    ],
  ]);
  CALLING_PLAN_NAME_MAP: Map<string, string> = new Map([
    ['DEDICATED_NUMBER', 'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.dedicated_number'],
    [
      'DEDICATED_NUMBER_PER_MINUTE',
      'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.dedicated_number_per_minute',
    ],
    [
      'DEDICATED_NUMBER_BUNDLED_MINUTES',
      'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.dedicated_number_bundled_minutes',
    ],
    ['SHARED_CALLING', 'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.shared_calling'],
  ]);

  columnVisibilityFilterOptions: FilterSelectOption[] = [
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: false,
    },
    // Teams Account
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.user.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.user.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: false,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.display_name.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.display_name.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: true,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.phone_number.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.phone_number.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: true,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.account_type.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: true,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.pstn_type.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: true,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.calling_plan.label',
      groupBy: 'tkey;reports.microsoft.teams_pstn_calling_usage.teams_account.label',
      hide: true,
    },
    // Insights
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_calling_product.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.ai_insights.label',
      hide: false,
    },
    {
      value: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.label',
      label: 'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.ai_insights.label',
      hide: false,
    },
    // Monthly Utilization
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_calls.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_calls.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
      hide: false,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_calls.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_calls.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
      hide: true,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_minutes.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.incoming_minutes.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
      hide: true,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_minutes.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.outgoing_minutes.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.monthly_usage.label',
      hide: false,
    },
    // Total Utilization
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_calls.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_calls.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
      hide: true,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_calls.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_calls.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
      hide: true,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_minutes.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.incoming_minutes.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
      hide: true,
    },
    {
      value: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_minutes.label',
      label: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.outgoing_minutes.label',
      groupBy: 'tkey;migrations.cisco.teams_pstn_calling_usage.total_usage.label',
      hide: true,
    },
  ];
  defaultVisibleColumns: FilterSelectOption[] = [...this.columnVisibilityFilterOptions].filter(
    (option: FilterSelectOption) => !option.hide,
  );

  private _tenantId: string;
  private static _exportButtonId = 'exportReport';

  ngOnInit(): void {
    this._tenantId = this._route.snapshot.paramMap.get('tenantId');

    this._breadcrumbService.updateBreadcrumbs([
      { label: 'tkey;reports.microsoft.teams_pstn_calling_usage.breadcrumb' },
    ]);

    this._initFilterOptionLists();

    const reportObs = this._pstnCallingUsageReportResource.get(this._tenantId).pipe(
      map((report) => {
        this._initDatatable(report.teamsPstnCallingUsageDetailsPerUser);
      }),
    );
    const metadataObs = this._callRecordsDatabaseEntriesResource.getInfo(this._tenantId).pipe(
      map((info) => {
        this._buildCallRecordsInfo(info);
      }),
    );
    const statusObs = this._callRecordsDatabaseEntriesResource.getStatus(this._tenantId).pipe(
      map((status) => {
        this._setBottomNavTimestamp(status.lastRunTime);
        if (status.error) {
          this._handleJobError(status.error);
        }
      }),
    );

    combineLatest([reportObs, metadataObs, statusObs]).subscribe(() => {
      if (!this.errorAlert && this.datatableRows.length === 0) {
        this.errorAlert = 'tkey;reports.microsoft.teams_pstn_calling_usage.info_box.no_call_records_error';
      }
      this.isLoading = false;
      this._initBottomNav();
    });
  }

  isAccountTypeMatch = (filter: string[], row: PstnCallingUsageRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.accountType);

  suggestCallingProductFilterMatch = (filter: string[], row: PstnCallingUsageRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.suggestedProduct);

  pstnTypeMatch = (filter: string[], row: PstnCallingUsageRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.pstnType);

  callingPlanMatch = (filter: string[], row: PstnCallingUsageRow): boolean =>
    this._datatableFilterService.filterMultiSelectValues(filter, row.callingPlan);

  isSuggestedChangesMatch = (filter: string[], row: PstnCallingUsageRow): boolean => {
    return this._datatableFilterService.filterMultiSelectValues(filter, row.suggestedChanges);
  };

  matchesSecondaryDataFn = (searchValue: string, row: PstnCallingUsageRow): boolean => {
    if (!searchValue) {
      return true;
    }
    const formattedSearchValue = numericizePhoneNumberNoCountry(searchValue);
    const formattedRowNumber = numericizePhoneNumberNoCountry(row.phoneNumber);

    return formattedRowNumber.includes(formattedSearchValue);
  };

  onTableResultsChanged(): void {
    this._updateFilterCounts();
  }

  private _initDatatable(data: TeamsPstnCallingUsageDetail[]): void {
    this.datatableRows = data.map((pstnCallingUsageDetail: TeamsPstnCallingUsageDetail) => {
      return {
        user: pstnCallingUsageDetail.userPrincipalName,
        displayName: pstnCallingUsageDetail.displayName,
        accountType:
          this._translateService.instant(this.ACCOUNT_TYPE_NAME_MAP.get(pstnCallingUsageDetail.accountType)) ?? '',
        pstnType:
          this._translateService.instant(this.PSTN_TYPE_NAME_MAP.get(pstnCallingUsageDetail.pstnConnectivityType)) ??
          '',
        callingPlan: this._getCallingPlan(pstnCallingUsageDetail),
        suggestedProduct:
          this._translateService.instant(
            this.SUGGESTED_CALLING_PRODUCT_MAP.get(pstnCallingUsageDetail.insights?.suggestedProduct),
          ) ?? '',
        phoneNumber: this._telephoneNumberFilter.transform(pstnCallingUsageDetail.lineUri),
        numberOfCalls: pstnCallingUsageDetail.totalInboundCalls + pstnCallingUsageDetail.totalOutboundCalls,
        totalDuration: Math.round(
          pstnCallingUsageDetail.totalOutboundCallDurationInMinutes +
            pstnCallingUsageDetail.totalInboundCallDurationInMinutes,
        ),
        suggestedChanges: this._getSuggestedChangesText(pstnCallingUsageDetail),
        monthlyUtilizationIncomingCalls: pstnCallingUsageDetail.monthlyInboundCalls,
        monthlyUtilizationOutgoingCalls: pstnCallingUsageDetail.monthlyOutboundCalls,
        monthlyUtilizationIncomingMinutes: pstnCallingUsageDetail.monthlyInboundCallDurationInMinutes,
        monthlyUtilizationOutgoingMinutes: pstnCallingUsageDetail.monthlyOutboundCallDurationInMinutes,
        totalUtilizationIncomingCalls: pstnCallingUsageDetail.totalInboundCalls,
        totalUtilizationOutgoingCalls: pstnCallingUsageDetail.totalOutboundCalls,
        totalUtilizationIncomingMinutes: pstnCallingUsageDetail.totalInboundCallDurationInMinutes,
        totalUtilizationOutgoingMinutes: pstnCallingUsageDetail.totalOutboundCallDurationInMinutes,
        hardcodedTooltips: {
          suggestedChanges: this._getSuggestedChangesTooltip(pstnCallingUsageDetail),
        },
        html: {
          suggestedChanges: this._getSuggestedChangesHtml(pstnCallingUsageDetail),
        },
      };
    });

    if (data.length > 0) {
      this._updateFilterCounts();
    }
  }

  private _initFilterOptionLists(): void {
    this.accountTypeFilterOptions = Array.from(this.ACCOUNT_TYPE_NAME_MAP.values()).map((tkey) => {
      const label = this._translateService.instant(tkey);
      return { value: label, label, count: 0 };
    });
    this.suggestedCallingProductFilterOptions = Array.from(this.SUGGESTED_CALLING_PRODUCT_MAP.values())
      .map((tkey) => this._translateService.instant(tkey))
      .map((label) => ({ value: label, label, count: 0 }));
    this.pstnTypeFilterOptions = Array.from(this.PSTN_TYPE_NAME_MAP.values())
      .map((tkey) => this._translateService.instant(tkey))
      .map((label) => ({ value: label, label, count: 0 }));
    this.callingPlanFilterOptions = Array.from(this.CALLING_PLAN_NAME_MAP.values())
      .map((tkey) => this._translateService.instant(tkey))
      .map((label) => ({ value: label, label, count: 0 }));
    this._computeSuggestedChangesFilterOptions();
  }

  private _computeSuggestedChangesFilterOptions(): void {
    const noChangeLabel = this._translateService.instant(
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.no_change',
    );
    const undeterminedLabel = this._translateService.instant(
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.undetermined',
    );

    const notApplicableLabel = this._translateService.instant(
      this.SUGGESTED_CALLING_PRODUCT_MAP.get(SuggestedProduct.NOT_APPLICABLE),
    );

    const suggestedProductLabels: string[] = Array.from(this.SUGGESTED_CALLING_PRODUCT_MAP.values()).map((tkey) =>
      this._translateService.instant(tkey),
    );

    const options = [
      noChangeLabel,
      undeterminedLabel,
      ...suggestedProductLabels.filter((label) => label !== notApplicableLabel),
    ];

    this.suggestedChangesFilterOptions = options.map((label) => ({ value: label, label, count: 0 }));
  }

  private _updateFilterCounts(): void {
    if (!this.datatableComponent?.filteredData) {
      return;
    }

    const activeFilters = this.datatableComponent.filteredData;

    const accountTypeCounts = new Map<string, number>();
    const suggestedProductCounts = new Map<string, number>();
    const pstnTypeCounts = new Map<string, number>();
    const callingPlanCounts = new Map<string, number>();
    const suggestedChangesCounts = new Map<string, number>();

    activeFilters.forEach((row) => {
      accountTypeCounts.set(row.accountType, (accountTypeCounts.get(row.accountType) || 0) + 1);
      suggestedProductCounts.set(row.suggestedProduct, (suggestedProductCounts.get(row.suggestedProduct) || 0) + 1);
      pstnTypeCounts.set(row.pstnType, (pstnTypeCounts.get(row.pstnType) || 0) + 1);
      callingPlanCounts.set(row.callingPlan, (callingPlanCounts.get(row.callingPlan) || 0) + 1);
      suggestedChangesCounts.set(row.suggestedChanges, (suggestedChangesCounts.get(row.suggestedChanges) || 0) + 1);
    });

    this.accountTypeFilterOptions.forEach((opt) => {
      opt.count = accountTypeCounts.get(opt.value) ?? 0;
    });
    this.suggestedCallingProductFilterOptions.forEach((opt) => {
      opt.count = suggestedProductCounts.get(opt.value) ?? 0;
    });
    this.pstnTypeFilterOptions.forEach((opt) => {
      opt.count = pstnTypeCounts.get(opt.value) ?? 0;
    });
    this.callingPlanFilterOptions.forEach((opt) => {
      opt.count = callingPlanCounts.get(opt.value) ?? 0;
    });
    this.suggestedChangesFilterOptions.forEach((opt) => {
      opt.count = suggestedChangesCounts.get(opt.value) ?? 0;
    });

    this._changeDetectorRef.detectChanges();
  }

  private _buildCallRecordsInfo(info: CallRecordsDatabaseEntriesInfo): void {
    this.infoBoxParams = {
      numCallRecords: String(info.totalNumberOfCallRecords),
      startDate: this._datePipe.transform(info.oldestCallRecordDate, 'MMMM d, y') || '',
      endDate: this._datePipe.transform(info.newestCallRecordDate, 'MMMM d, y') || '',
      numDays: String(info.totalDaysOfCallRecords),
    };
  }

  private _getSuggestedChangesTooltip(detail: TeamsPstnCallingUsageDetail): string {
    const ageInDays = this._getOldestCdrAgeInDays(detail);

    const usersOldestCdrTimestamp = this._datePipe.transform(detail.oldestCdrTimestamp, 'MMMM d, y');
    const displayOldestCdrTimestamp = usersOldestCdrTimestamp ?? this._translateService.instant('tkey;global.none');

    if (ageInDays && ageInDays < 30) {
      return this._translateService.instant(
        'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.less_than_30_days.tooltip',
        { usersOldestCdrTimestamp: displayOldestCdrTimestamp },
      );
    }

    const insight = detail.insights.downgradeOrUpgradeInsight;

    if (insight === DowngradeOrUpgradeInsight.UNDETERMINED) {
      return this._translateService.instant(
        'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.undetermined.tooltip',
      );
    }

    return this._translateService.instant(
      'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.older_than_30_days.tooltip',
      { usersOldestCdrTimestamp: displayOldestCdrTimestamp },
    );
  }

  private _getCallingPlan(detail: TeamsPstnCallingUsageDetail): string {
    if (detail.numberType === PstnCallingNumberType.SHARED_CALLING) {
      return this._translateService.instant(this.CALLING_PLAN_NAME_MAP.get('SHARED_CALLING')) ?? '';
    }

    if (detail.numberType === PstnCallingNumberType.LINE_URI) {
      const connectivity = detail.pstnConnectivityType;
      if (
        connectivity === PstnConnectivityType.DIRECT_ROUTING ||
        connectivity === PstnConnectivityType.OPERATOR_CONNECT
      ) {
        return this._translateService.instant(this.CALLING_PLAN_NAME_MAP.get('DEDICATED_NUMBER')) ?? '';
      }

      if (connectivity === PstnConnectivityType.MICROSOFT_CALLING_PLANS) {
        const planType = detail.microsoftCallingPlanType;
        if (planType === MicrosoftCallingPlanType.PAYG) {
          return this._translateService.instant(this.CALLING_PLAN_NAME_MAP.get('DEDICATED_NUMBER_PER_MINUTE')) ?? '';
        }
        if (planType === MicrosoftCallingPlanType.CALLING_PLAN) {
          return (
            this._translateService.instant(this.CALLING_PLAN_NAME_MAP.get('DEDICATED_NUMBER_BUNDLED_MINUTES')) ?? ''
          );
        }
      }
    }
    return '';
  }

  private _getSuggestedChangesText(detail: TeamsPstnCallingUsageDetail): string {
    const insight = detail.insights.downgradeOrUpgradeInsight;
    const productLabel = this._getProductLabel(detail.insights.suggestedProduct);

    const suggestedChangesText: Record<DowngradeOrUpgradeInsight, string> = {
      [DowngradeOrUpgradeInsight.NONE]: this._translateService.instant(
        'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.no_change',
      ),

      [DowngradeOrUpgradeInsight.UNDETERMINED]: this._translateService.instant(
        'tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.undetermined',
      ),

      [DowngradeOrUpgradeInsight.UPGRADE]: productLabel,

      [DowngradeOrUpgradeInsight.DOWNGRADE]: productLabel,
    };

    return suggestedChangesText[insight];
  }

  private _getSuggestedChangesHtml(detail: TeamsPstnCallingUsageDetail): string {
    const insight = detail.insights.downgradeOrUpgradeInsight;
    const productLabel = this._getProductLabel(detail.insights.suggestedProduct);
    const iconClass = this._getSuggestedChangesIcon(detail);

    const suggestedChangesHtml: Record<DowngradeOrUpgradeInsight, string> = {
      [DowngradeOrUpgradeInsight.NONE]: `<i class="${iconClass} ms-3 me-1"></i><span>${this._translateService.instant('tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.no_change')}</span>`,

      [DowngradeOrUpgradeInsight.UNDETERMINED]: `<i class="${iconClass} ms-3 me-1"></i><span>${this._translateService.instant('tkey;reports.microsoft.teams_pstn_calling_usage.suggested_changes.undetermined')}</span>`,

      [DowngradeOrUpgradeInsight.UPGRADE]: productLabel
        ? `<i class="${iconClass} ms-3 me-1"></i><span>${productLabel}</span>`
        : '',

      [DowngradeOrUpgradeInsight.DOWNGRADE]: productLabel
        ? `<i class="${iconClass} ms-3 me-1"></i><span>${productLabel}</span>`
        : '',
    };

    return suggestedChangesHtml[insight];
  }

  private _getSuggestedChangesIcon(detail: TeamsPstnCallingUsageDetail): string {
    const insight = detail.insights?.downgradeOrUpgradeInsight;

    const ageInDays = this._getOldestCdrAgeInDays(detail);

    if (ageInDays && ageInDays < 30) {
      return this.ziroIcons.UNDEFINED;
    }

    const iconByInsight: Record<DowngradeOrUpgradeInsight, string> = {
      [DowngradeOrUpgradeInsight.NONE]: this.ziroIcons.CIRCLE_CHECK_SOLID,
      [DowngradeOrUpgradeInsight.UNDETERMINED]: this.ziroIcons.UNDEFINED,
      [DowngradeOrUpgradeInsight.UPGRADE]: this.ziroIcons.UPGRADE,
      [DowngradeOrUpgradeInsight.DOWNGRADE]: this.ziroIcons.DOWNGRADE,
    };

    return iconByInsight[insight];
  }

  private _getProductLabel(product: SuggestedProduct): string {
    return this._translateService.instant(this.SUGGESTED_CALLING_PRODUCT_MAP.get(product));
  }

  private _initBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [
      {
        id: PstnCallingUsageReportComponent._exportButtonId,
        dataAutomation: PstnCallingUsageReportComponent._exportButtonId,
        label: 'tkey;reports.microsoft.dial_plan_inventory.export_to_excel',
        icon: this.ziroIcons.EXPORT,
        buttonClass: this.buttonStyles.PRIMARY,
        cb: () => {
          this._onExportClick();
        },
      },
    ];

    this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _handleJobError(error: string): void {
    if (error.toLowerCase().includes('read access to pstn calling details not permitted')) {
      this.errorAlert = 'tkey;reports.microsoft.teams_pstn_calling_usage.info_box.permissions_error';
    } else {
      this.errorAlert = 'tkey;reports.microsoft.teams_pstn_calling_usage.info_box.standard_error';
    }
  }

  private _setBottomNavTimestamp(timestamp: string): void {
    if (!timestamp) {
      return;
    }
    this._bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: false,
        helpText: `
              <i class="${this.ziroIcons.TIMESTAMP}"></i>
              ${this._translateService.instant(
                'tkey;reports.microsoft.teams_pstn_calling_usage.last_updated.label',
              )} <strong>${this._dateAgoPipe.transform(timestamp)}</strong>
            `,
      }),
    );
  }

  private _setPending(id: string, setting: boolean): void {
    this._bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: id,
        state: {
          pending: setting,
          buttonDisableState: { disabled: setting, tooltipKey: '' },
        },
      }),
    );
  }

  private _onExportClick(): void {
    this._setPending(PstnCallingUsageReportComponent._exportButtonId, true);

    this._pstnCallingUsageReportResource.export(this._tenantId).subscribe(() => {
      this._setPending(PstnCallingUsageReportComponent._exportButtonId, false);
      this._toastService.push(
        ToastTypes.SUCCESS,
        this.ziroIcons.DOWNLOAD,
        'tkey;reports.xlsx_export.downloaded_toast.title',
        'tkey;reports.xlsx_export.downloaded_toast.message',
      );
    });
  }

  private _getOldestCdrAgeInDays(detail: TeamsPstnCallingUsageDetail): number | null {
    const timestamp = detail.oldestCdrTimestamp;
    if (!timestamp) {
      return null;
    }

    const oldest = moment(new Date(timestamp));
    return oldest.isValid() ? moment().diff(oldest, 'days') : null;
  }
}
